import { useEffect, useRef } from 'react';
import { FAQ_ALL_OPTION, type FaqOption } from '../constants/faq.ts';

interface BoardSubCategoryFilterProps {
  options: readonly FaqOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** 칩 묶음 이름 (스크린리더용) */
  ariaLabel?: string;
}

const CHIP_CLASS = {
  base: 'inline-flex h-9 items-center justify-center whitespace-nowrap rounded-full border px-4 text-[12px] font-semibold transition-colors',
  active:
    'border-neutral-950 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950',
  inactive:
    'border-black/15 bg-white text-neutral-950/60 hover:border-neutral-950 hover:text-neutral-950 dark:border-white/15 dark:bg-dark-card dark:text-white/60 dark:hover:border-white dark:hover:text-white',
} as const;

export function BoardSubCategoryFilter({
  options,
  value,
  onChange,
  ariaLabel = '하위 분류',
}: BoardSubCategoryFilterProps) {
  const selectedChipRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedChipRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
    });
  }, [value]);

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="overflow-x-auto scroll-smooth [scrollbar-width:none] motion-reduce:scroll-auto [&::-webkit-scrollbar]:hidden"
    >
      <div className="flex w-max min-w-full flex-nowrap gap-2 px-1 py-4 sm:w-auto sm:flex-wrap">
        {[FAQ_ALL_OPTION, ...options].map((option) => {
          const isActive = value === option.value;
          return (
            <button
              ref={isActive ? selectedChipRef : undefined}
              key={option.value ?? FAQ_ALL_OPTION.label}
              type="button"
              aria-pressed={isActive}
              onClick={() => onChange(option.value)}
              className={`${CHIP_CLASS.base} ${
                isActive ? CHIP_CLASS.active : CHIP_CLASS.inactive
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
